import {
    container,
    ProductService,
    Wishlist,
    WishlistService,
    LoyaltyMemberPointsService,
    LoyaltyMemberPoints,
    LoyaltyProgramService,
    RdssCartService,
    RdssSimpleCart,
    ILoyalty,
    Unit,
    Price,
} from '@rdss/client'
import { Translation } from '@rdss/client/dist/catalog/types/translation';
import { scopify, toMoney } from '@rdss/core'
import { AddToCartEvent } from '@rdss/elements'
import { Build, Component, Element, h, Host, Prop } from '@stencil/core'
import { JSX, Listen, State } from '@stencil/core/internal'
import { productSelect, select } from '~/selectors'
import { store, trans, addToCart } from '~/store'
import { DetailProduct } from '~/types'
import { getLoyaltyPointsToEarn, getNonQualifyingPoints, wasCalledFromShop, trackCitrusAd, isSponsoredProduct } from '~/utils';
import pdfIcon from './assets/pdf-file-icon.svg'

declare const _satellite: { track }
declare const window: { rdssTracker, outerWidth }

@Component({
    tag: 'zr-product-detail-page',
    styleUrl: 'zr-product-detail-page.scss',
    scoped: true,
})

/**
 * The zooroyal product detail page.
 */
export class ZrProductDetailPage {
    /**
     * Initial Feature Toggle Data from ESI.
     */
    @Prop() initialFeatureToggle: string

    /**
     * Load wishlist data on page load.
     */
    @Prop() loadWishlistOnRequest = false

    /**
     * The article id aka product id.
     */
    @Prop() articleId: number

    /**
     * The article id aka product id.
     */
    @Prop() sku: string

    /**
     * Currency identifier used for currency.
     */
    @Prop() currency = 'EUR'

    /**
     * Quantity used for quantity select.
     */
    @Prop({ mutable: true }) quantity = 1

    /**
     * Max number of items to select as quantity in quantity selector.
     *
     * @widgetFieldLabel Maximale Anzahl
     */
    @Prop({ mutable: true }) maxQuantity? = 100

    /**
     * Defines if button is disabled.
     */
    @Prop() quantityDisabled?: boolean

    /**
     * Label for the quantity select field.
     */
    @Prop({ mutable: true }) labelAmount = 'Anzahl'

    @Prop() readonly productService = container.resolve(ProductService)
    @Prop() readonly wishlistService = container.resolve(WishlistService)

    /**
     * Loyalty Points service.
     */
    @Prop() readonly loyaltyMemberPointsService = container.resolve(LoyaltyMemberPointsService)

    /**
     * Loyalty Points Factor service.
     */
    @Prop() readonly loyaltyPointsFactorService = container.resolve(LoyaltyProgramService)

    /**
     * The cart service.
     */
    @Prop() readonly cartService = container.resolve(RdssCartService)

    /**
     * Loyalty Points.
     */
    @State() memberPoints: number = null

    @Element() host: HTMLElement

    /**
     * Describes whether product is on wishlist or not.
     */
    @State() productOnWishlist = false

    /**
     * Points Factor.
     */
    @State() loyaltyPointsFactor: number

    /**
     * The product state.
     */
    @State() private product: DetailProduct

    @State() private itemAdded = false

    @State() private isLoading = false

    @State() private showVariantSwitch = !/\bsponsored=\b/.test(location.search)

    @State() private citrusAdId: string | null = (new URLSearchParams(location.search)).get('sponsored')

    /**
     * Feature toggle object.
     */
    @State() featureToggle: {
        displayLoyaltyPoints: boolean
    }

    /**
     * Locale used for currency/number.
     */
    private locale = 'de-DE'

    /**
     * The animation duration of the add-to-cart animation in milliseconds.
     */
    private addToCartAnimationDuration = 1000

    /**
     * Wishlist items collection.
     */
    private wishlist: Wishlist

    /**
     * Data object to aggregate all the necessary product-data for the zr-accordion-component.
     */
    private aggregatedProductData = []

    /**
     * Data object to compile all the necessary data for the zr-usp-component.
     */
    private uspData = []

    /**
     * Product gross Price.
     */
    @State() private productPriceGross: number

    /**
     * Add Product to cart if add-to-cart component
     * fires the addToCartClicked Event.
     */
    @Listen('addToCartClicked')
    async handleAddToCart({ detail }: AddToCartEvent) {
        const cartItem = store.state.cart?.items?.find(
            (item) => {
                return item.sku === this.product.ordernumber
            })
        const newQuantity = detail.quantity + (cartItem?.quantity || 0)
        const newDetail = {
            quantity: newQuantity,
            orderNumber: this.product.ordernumber,
            context: 'product-detail',
            useLoyaltyPoints: !!this.product?.loyalty?.points,
        } as {
            quantity: number,
            orderNumber: string,
            context: string,
            useLoyaltyPoints: boolean
        }

        const response = await addToCart(newDetail, {
            sku: this.product.ordernumber,
            sponsored: isSponsoredProduct(this.product),
            brand: this.product.manufacturer.name,
            name: this.product.title,
            position: 1,
            price: {
                gross: this.product.price.gross,
                grossPseudo: this.product.price.grossPseudo,
            },
        });

        if (response) {
            this.itemAdded = true
            await this.getMemberPoints()
            setTimeout(() => (this.itemAdded = false), this.addToCartAnimationDuration)
        }

        if (wasCalledFromShop() && !this.showVariantSwitch) {
            await trackCitrusAd('click', { ...this.product, citrusAdId: this.citrusAdId })
        }
    }

    /**
     * Handles the variant changes.
     */
    @Listen('variantChanged')
    handleVariantChange({ detail }: CustomEvent<{ sku: string }>) {
        this.loadProduct(detail.sku)
    }

    /**
     * Handles the wishlist click.
     */
    @Listen('addToWishlistClicked')
    async handleProductToWishlist() {
        const { ordernumber } = this.product

        await this.wishlistService.findOne(ordernumber)
            .then(({ ok }) => {
                this.productOnWishlist = ok
                _satellite?.track('wlAdd', ordernumber)
            })
            .catch((error) => {
                if (error.status !== 403) return
            })
    }

    /**
     * Animate Price Change.
     */
    private animatePrice() {
        const startTime = Date.now()
        const duration = 1000

        /**
         * Sub function for Animation.
         */
        const animate = () => {
            const currentTime = Date.now()
            const elapsedTime = currentTime - startTime

            if (elapsedTime < duration) {
                const progress = elapsedTime / duration
                const targetGross = this.quantity * this.product.price.gross
                this.productPriceGross = this.productPriceGross + (targetGross - this.productPriceGross) * progress

                requestAnimationFrame(animate)
            } else {
                this.productPriceGross = this.quantity * this.product.price.gross
            }
        }

        animate()
    }

    /**
     * Sorts the product variants by price.
     *
     * @param {object} variants The variants to sort.
     * @private
     */
    private sortVariantsByPriceAscending(variants) {
        try {
            return [...variants].sort((a, b) => {
                if (a.price.net < b.price.net) {
                    return -1
                }
                if (a.price.net > b.price.net) {
                    return 1
                }
                return 0
            })
        } catch (error) {
            return variants
        }
    }

    /**
     * Get Member Loyalty Points.
     */
    private async getMemberPoints(withDelay = true) {
        if(!this.product?.loyalty?.points || !this.checkUserLoggedIn() || Build.isServer) return
        /**
         * I must write this comment because of the brilliant idea of describing functions where the function name
         * already explains what it does. It gets the points from backend -> salesforce
         * and it's just a wrapper function so I can use it in setTimeout and without it because if we dont use
         * timeout the points are not updated (available points - rewards in cart) in session.
         * Well good we have these comments no one would know what this function does.
         */
        const getPoints = () => {
            this.loyaltyMemberPointsService.find()
                .then((data: any) => {
                    const memberPoints = data as LoyaltyMemberPoints
                    this.memberPoints = getNonQualifyingPoints(memberPoints)
                })
                .catch(e => console.debug('FETCH DEBUG: ', e))
        }
        if (withDelay) {
            setTimeout(getPoints, 500);
        } else {
            getPoints();
        }
    }

    /**
     * Get Points Earn Factor.
     */
    private async getPointsEarnFactor(){
        this.loyaltyPointsFactorService.find()
            .then((data: any) => {
                this.loyaltyPointsFactor = data?.loyaltyOrderPointsFactor
            })
            .catch(e => console.debug('FETCH DEBUG: ', e))
    }

    /**
     * Stencils lifecycle hook. Triggers when the component will be
     * load, and before the component is rendered.
     */
    async componentWillLoad() {
        await this.loadProduct()
        await this.getPointsEarnFactor()
        await this.getMemberPoints()

        if (this.initialFeatureToggle) {
            this.featureToggle = typeof this.initialFeatureToggle === 'string'
                ? JSON.parse(this.initialFeatureToggle)
                : this.initialFeatureToggle
        }

        if (this.product?.loyalty?.points) {
            this.showVariantSwitch = false;
        }

        // Load cart
        store.state.cart = (await this.cartService.getCart() as unknown as RdssSimpleCart)
    }

    /**
     * Aggregates the product-detail information for the zr-accordion component.
     */
    private aggregateProductDetailforAccordion() {
        this.aggregatedProductData = []
        const scoping = 'sc-zr-product-detail-page'
        const {
            content,
            translation,
            manufacturer,
            productLines,
            distributor,
        } = this.product

        const {
            composition,
            feedingRecommendation,
            size,
            technicalDetails,

        } = select(this.product.attributes, translation)

        const description = content?.description.concat(
            `<p><strong>${this.product.translation.ArticleNumber}</strong><br>${this.product.ordernumber}</p>`
        )

        const htmlTags = [
            'table', 'thead', 'tbody', 'th', 'tr', 'td', 'p', 'br', 'a', 'img',
            'ul', 'li', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'iframe', 'b', 'em', 'u', 'i',
        ]

        if (description) {
            this.aggregatedProductData.push(
                {
                    title: translation?.DetailTabsDescription,
                    content: scopify(description, htmlTags, scoping),
                }
            )
        }

        if (size) {
            this.aggregatedProductData.push(
                {
                    title: translation?.DetailTabsFeedingSizeInformation,
                    content: scopify(size, htmlTags, scoping),
                }
            )
        }

        if (technicalDetails) {
            this.aggregatedProductData.push(
                {
                    title: translation?.DetailTabsFeedingTechnicalDetails,
                    content: scopify(technicalDetails, htmlTags, scoping),
                }
            )
        }

        if (composition) {
            this.aggregatedProductData.push(
                {
                    title: translation?.DetailTabsComposition,
                    content: scopify(scopify(composition, htmlTags, scoping), htmlTags, scoping),
                }
            )
        }

        if (feedingRecommendation) {
            this.aggregatedProductData.push(
                {
                    title: translation?.DetailTabsFeedingRecommendation,
                    content: scopify(feedingRecommendation, htmlTags, scoping),
                }
            )
        }

        // manufacturer information for product-info aggregation
        this.aggregatedProductData.push(
            {
                title: translation?.DetailTabsBrand,
                content:
                    <zr-manufacturer
                        manufacturer={manufacturer}
                        productLines={productLines}
                        labelProductSortiment={translation?.ProductSortiment}
                        distributor={distributor || null}
                        labelDistributor={translation?.SupplierInformation}
                    />,
            }
        )

        // product information dangerous goods declaration
        if (this.product?.dangerousGoodsDeclarationPdf) {

            this.aggregatedProductData.push(
                {
                    title: translation?.DetailTabsPdfDownload,
                    content:
                        <a
                            class="pdf-link"
                            href={this.product?.dangerousGoodsDeclarationPdf}
                            target="_blank"
                            title={translation?.DetailTabsPdfDownloadDangerousGoods}>
                            <img class="pdf-img-icon" src={pdfIcon} alt="pdf Icon"/>
                            {translation?.DetailTabsPdfDownloadDangerousGoods}
                        </a>,
                }
            )
        }
    }

    /**
     * Aggregates the data for the zr-usp component.
     */
    private aggregateUspData() {
        const { shipping, price, translation } = this.product

        const shippingFreeAmount = toMoney(shipping?.shippingFree, price?.currency, this.locale)
        this.uspData = [
            {
                icon: 'van',
                text: `${translation?.NoFreeShippingText} ${shippingFreeAmount}`,
            },
            {
                icon: 'card',
                text: translation?.paymentSafe,
            },
            {
                icon: 'rewe-pin',
                text: translation?.NoFreeShippingTextReweMarket,
            },
            {
                icon: 'package',
                text: translation?.freeReturnShipment,
            },
        ]
    }

    /**
     * Load wishlist items.
     */
    private loadWishlist = () =>
        Build.isBrowser &&
        this.loadWishlistOnRequest &&
        this.wishlistService
            .getAll()
            .then(({ data }) => {
                this.wishlist = data
                this.productOnWishlist = this.isProductOnWishlist()
            })

    /**
     * Check if product is in wishlist.
     *
     * @returns {boolean} Whether product is on wishlist.
     */
    private isProductOnWishlist(): boolean {
        return this.loadWishlistOnRequest && this.wishlist?.filter((value) => {
            return value.ordernumber === this.sku
        }).length > 0
    }

    /**
     * Load Product by sku.
     */
    private loadProduct = async (sku = this.sku) => {
        this.isLoading = true
        await this.productService
            .findBySku(sku)
            .then(async ({ data }) => {
                this.product = data
                this.locale = data?.translation?.CurrentLocale
                this.labelAmount = data?.translation?.OrderItemColumnQuantity
                this.productPriceGross = data?.price?.gross
                this.setMaxQuantity()
                await this.loadWishlist()
            })
            .catch(error => console.error(ZrProductDetailPage.name, `Error while getting product ${sku}`, error))
            .finally(() => {
                this.isLoading = false
            })
    }

    /**
     * Sets the max quantity for the quantity select.
     */
    private setMaxQuantity({ unit, stock } = this.product) {
        if (unit.maxPurchase) {
            this.maxQuantity = unit?.maxPurchase
        } else {
            this.maxQuantity = stock > 0 && stock < 100 ? stock : 100
        }
        if (this.quantity > this.maxQuantity) {
            this.quantity = 1
        }
        if (Build.isBrowser) this.animatePrice()
    }

    /**
     * Handler for quantity state change.
     *
     * @param {Event} event
     */
    private handleQuantityChange = ({ detail }: CustomEvent) => {
        this.quantity = Number(detail.value)

        if (this.quantity > 1) {
            this.animatePrice()
        } else {
            this.productPriceGross = this.product.price.gross
        }
    }

    /**
     * Scrolls to the rating element when star icon is clicked.
     */
    private goToRatings = (): void => {
        const elements = document.getElementsByTagName('zr-rating')
        if (elements.length > 0) {
            elements[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
    }

    /**
     * Renders the quantity selector.
     *
     * @returns {JSX.Element} - Returns the quantity selector.
     */
    private quantityBox = (): JSX.Element => {
        const options = Array.from(Array(this.maxQuantity).keys()).map(i => ({
            value: String(i + 1),
            text: String(i + 1),
        }))

        return (
            <vx-select
                aria-label={this.labelAmount}
                aria-disabled={this.quantityDisabled}
                disabled={!this.product.isAvailable}
                options={options}
                id="zr-product-detail-quantity-select"
                onChanged={this.handleQuantityChange}/>
        )
    }

    // temp function until final api
    private checkUserLoggedIn = (): boolean => {
        return window?.rdssTracker?.pageDataState?.user?.status === 'logged-in'
    }

    /**
     * GO TO CLUB LINK.
     */
    private goToClub = () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.location.href = '/account';
    }

    /**
     * Lifecycle method to trigger after load.
     */
    componentDidLoad() {
        if (Build.isBrowser && !this.product?.loyalty?.points) {
            this.host.style.setProperty('--vx-variant-switch-visibility', 'visible')
        }
    }

    /**
     * Render ADD To Card.
     */
    private renderAddToCart = (translation: Translation) => {
        if (this.product?.loyalty?.points && this.memberPoints === null) {
            return (
                <div class='zr-link-to-club-button'>
                    <vx-button
                        aria-label="Jetzt ZooRoyal Club Mitglied werden"
                        raised={true}
                        fullwidth={true}
                        onClick={this.goToClub}
                    >
                        Jetzt ZooRoyal Club Mitglied werden
                    </vx-button>
                </div>
            )
        }
        return (
            <vx-add-to-cart
                itemAdded={this.itemAdded}
                loading={store.state.cartIsLoading}
                maxQuantity={this.maxQuantity}
                disabled={
                    this.product?.loyalty?.points
                        ? (!this.product.isAvailable ||
                            (this.memberPoints < (this.product.loyalty.points * this.quantity)))
                        : !this.product.isAvailable
                }
                quantity={this.quantity}
                displayQuantitySelect={false}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    {this.product.isAvailable &&
                        <vx-icon icon="add_shopping_cart" style={{ display: 'flex' }} invert={true}></vx-icon>
                    }
                    <span class="addToCartTitle">
                        {!this.product.isAvailable ? translation?.ausverkauft : translation?.DetailBuyActionAddName}
                    </span>
                </div>
            </vx-add-to-cart>
        )
    }

    /**
     * Loyalty price info.
     */
    private renderLoyaltyPriceInfo = (loyalty: ILoyalty, translation: Translation) => {
        if(this.featureToggle?.displayLoyaltyPoints && !this.product?.loyalty?.points){
            return (
                <zr-loyalty-points-price-info
                    points={getLoyaltyPointsToEarn(this.productPriceGross, this.loyaltyPointsFactor)}
                    info={loyalty?.info}
                    infoUspBoxHead={translation?.becomeMember}
                    infoUspBoxText={translation?.memberSavings}
                    infoUspBoxLink={translation?.moreInformation}
                />
            )
        }
        return null;
    }

    /**
     * Render price with this function remove complexity.
     */
    private renderPrice = (price:Price, unit: Unit, translation: Translation) => {
        if(!this.product?.loyalty?.points){
            return (
                <div class="price-wrapper">
                    {(price.pseudoPercent > 0) && (
                        <zr-tooltip
                            showIcon={false}
                            isAnimated={false}
                            text={trans(
                                'frontend/detail/index.BadgeYouSave',
                                'du sparst %s',
                                [toMoney(price.grossPseudo - this.productPriceGross, price.currency, this.locale)]
                            )}
                        />
                    )
                    }
                    <zr-price
                        recommendedRetailPrice={price.grossPseudo}
                        gross={this.productPriceGross}
                        isTotalPrice={this.quantity > 1}
                        totalPriceLabel={translation.PriceCalcTotal}
                        currency={price.currency}
                        locale={this.locale}
                        weightUnit={unit.name}
                        referenceUnit={unit.referenceUnit?.toString()}
                        pseudoPercent={price.pseudoPercent}
                        referencePriceUnit={price.pricePerUnit}
                    />
                </div>
            )
        }
    }

    /**
     * Renders the component.
     */
    render() {
        if (!this.product) return

        const {
            manufacturer, title,
            details, unit, price,
            attributes, shipping, translation,
            properties, rating, loyalty,
            vetNotice, votes,
        } = this.product

        const media = Object.values(this.product?.media ?? [])
        const variants = details.map((detail) => {
            detail.title = this.product.translation.VariantTitle
            return detail
        })
        this.locale = this.product?.translation?.CurrentLocale ?? 'de-DE'
        this.aggregateProductDetailforAccordion()
        this.aggregateUspData()
        const { badges } = productSelect(attributes, properties)

        return (
            <Host>
                <main>
                    <vx-product-jsonld product={this.product}/>

                    <div class="product__supplier">
                        <a href={manufacturer?.link} title={manufacturer?.name}>
                            {manufacturer?.name}
                        </a>
                    </div>

                    {rating &&
                        <zr-rating-badge
                            value={rating.average}
                            count={rating.count}
                            locale={this.locale}
                            onClick={() => {
                                this.goToRatings()
                                _satellite?.track('AdHocEvent', 'click_reviews-score')
                            }}
                        />
                    }

                    <h1 innerHTML={title} class="product__name"></h1>

                    <div class="product__image-wrapper">
                        {badges &&
                            <zr-badge
                                class={media.length > 1 && 'is-above-thumbnail'}
                                bestBeforeBadgeHeading={translation?.BadgeBestBeforeDate}
                                hasBestBeforeBadge={badges.mhd}
                                bStockBadgeHeading={translation?.BadgeBStock}
                                hasBStockBadge={badges.bStock}
                                hasDiscountBadge={price.pseudoPercent >= 5}
                                discount={price.pseudoPercent}/>
                        }

                        {media.length > 0 && !this.isLoading &&
                            <vx-image-gallery
                                direction="ttb"
                                thumbnailSize={100}
                                images={media}/>
                        }
                    </div>

                    {attributes.core.eeiclass &&
                        <zr-energy-label
                            energyLevel={attributes.core.eeiclass}
                            productDetailsLabel={translation?.ProductDataSheet}
                            productDetailsLink={attributes.core.eeidatasheeturl}
                            energyImage={attributes.core.eeilabelurl}
                            modalHeadline={title}
                        />
                    }

                    <div class="zr-product-detail-variant-switch-wrapper">
                        {(this.showVariantSwitch)
                            ? <zr-variant-switch
                                showVariantOnly={!this.showVariantSwitch ? this.product.ordernumber : undefined}
                                snippets={translation}
                                variants={this.sortVariantsByPriceAscending(variants)}
                                locale={this.locale}
                            />
                            : <div class='price-wrapper-loyalty'>
                                {(this.product?.loyalty?.points) && (
                                    <zr-loyalty-price
                                        loyaltyPrice={this.product.loyalty.points * this.quantity}
                                    />
                                )
                                }
                            </div>
                        }
                    </div>

                    <div class="zr-to-cart-quantity-wrapper">
                        <label htmlFor={'zr-product-detail-quantity-select'}>{this.labelAmount}</label>
                        {this.quantityBox()}

                        {
                            (!this.product?.loyalty?.points) &&
                            (
                                this.checkUserLoggedIn()
                                    ? <zr-wishlist
                                        productOnWishlist={this.productOnWishlist}
                                        isLoggedIn={this.checkUserLoggedIn()}
                                    />
                                    : <vx-tooltip
                                        text={translation?.NoteInfoBox}
                                        clickOpen={true}>
                                        <div slot='vx-tooltip-slot'>
                                            <zr-wishlist
                                                productOnWishlist={this.productOnWishlist}
                                            />
                                        </div>
                                    </vx-tooltip>
                            )
                        }
                    </div>

                    {this.renderPrice(price, unit, translation)}

                    {this.renderAddToCart(translation)}

                    {this.renderLoyaltyPriceInfo(loyalty, translation)}

                    {
                        attributes.core.biocide === '1' &&
                        <zr-warning-notice
                            warning={translation?.Warning}
                            notice={translation?.Biocide}
                        />
                    }

                    <zr-shipping-information displayText={shipping.info}/>

                    <zr-usp data={this.uspData}/>

                    <div class="content">
                        <div class="product-description">
                            <zr-accordion-group data={this.aggregatedProductData}></zr-accordion-group>
                        </div>

                        <div>
                            {vetNotice &&
                                <picture>
                                    <source media="(max-width: 576px)" srcSet={vetNotice?.xs}/>
                                    <source media="(max-width: 768px)" srcSet={vetNotice?.sm}/>
                                    <source media="(max-width: 922px)" srcSet={vetNotice?.md}/>
                                    <img src={vetNotice?.lg} alt="vet notice"/>
                                </picture>
                            }
                        </div>
                    </div>

                    {
                        votes && votes.length > 0 &&
                        <zr-rating
                            productName={title}
                            reviews={votes}
                            rating={rating}
                            labelStars={translation?.DetailRatingStars}
                            labelStarMultiplePossible={translation?.DetailRatingStarMultiplePossible}
                            labelVotes={translation?.DetailRatingRating}
                            labelViewVotes={translation?.DetailRatingViewVotes}
                        />
                    }
                </main>
            </Host>
        )
    }
}
